/**
 * InvestorShare — public-facing landing page for a magic-link DD packet.
 *
 * URL: /share/dd/:token  (no auth — token IS the auth)
 *
 * Renders the startup snapshot + a "Open packet" CTA that streams the
 * PDF. The first call to GET /api/investor-share/{token} also records
 * a `preview` view event so the founder sees the investor opened the
 * landing page even if they don't download. Downloading records a
 * second `pdf_download` event.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, FileDown, ShieldCheck, Award, Lock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the token-fetch effect. */

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function InvestorShare() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${API_BASE}/api/investor-share/${token}`)
      .then(({ data }) => setMeta(data))
      .catch((e) => {
        const status = e?.response?.status;
        setError(
          status === 410 ? (e?.response?.data?.detail || 'This link is no longer active.')
          : status === 404 ? 'This link is invalid.'
          : 'Could not load the share. Please contact the sender.'
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  const download = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API_BASE}/api/investor-share/${token}/packet.pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (meta?.startup?.name || 'vigilant').replace(/\s+/g, '_');
      const suffix = meta?.kind === 'tear_sheet' ? 'tear_sheet' : 'dd_packet';
      a.download = `${safeName}_${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate PDF.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#0A0A0A] via-[#121212] to-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-md bg-[#8B3FC5] flex items-center justify-center font-bold">VE</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                Vigilant Entities
              </div>
              <div className="text-xs text-white/55">Sovereign capital · Investor share</div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold flex items-center gap-1.5">
            <Lock size={11} /> Single-recipient link
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-16">
        {loading && (
          <div className="text-center py-24">
            <Loader2 size={28} className="mx-auto animate-spin text-[#D4AF37]" />
            <p className="mt-3 text-sm text-white/55">Loading…</p>
          </div>
        )}

        {!loading && error && (
          <div
            data-testid="investor-share-error"
            className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-8 text-center"
          >
            <AlertTriangle size={32} className="mx-auto text-rose-400" />
            <h1 className="display text-2xl font-bold mt-4">Link unavailable</h1>
            <p className="mt-2 text-white/70 max-w-md mx-auto">{error}</p>
            <p className="mt-6 text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold">
              If you believe this is a mistake, please ask the sender for a fresh link.
            </p>
          </div>
        )}

        {!loading && meta && (
          <div data-testid="investor-share-card" className="space-y-6">
            {/* Patent eyebrow */}
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
              <Award size={11} /> Patent Pending · OOH™ Due-Diligence Packet
            </div>

            {/* Startup card */}
            <div className="rounded-2xl border border-[#8B3FC5]/30 bg-white/5 backdrop-blur p-8">
              <div className="text-xs uppercase tracking-[0.22em] text-white/50">
                Prepared for <span className="text-[#D4AF37] font-bold">{meta.investor_name}</span>
              </div>
              <h1 className="display text-3xl sm:text-4xl font-bold mt-2">
                {meta.startup?.name}
              </h1>
              {meta.startup?.tagline && (
                <p className="text-white/70 mt-2 max-w-2xl">{meta.startup.tagline}</p>
              )}

              {/* OOH score */}
              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <div className="text-[9px] uppercase tracking-[0.22em] text-white/45 font-bold">
                    OOH SCORE
                  </div>
                  <div className="display text-5xl font-bold text-[#D4AF37] mt-1">
                    {Math.round(meta.ooh_score || 0)}
                    <span className="text-base text-white/45 font-bold ml-1">/ 100</span>
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <div className="text-[9px] uppercase tracking-[0.22em] text-white/45 font-bold">
                    STAGE
                  </div>
                  <div className="text-sm font-bold mt-2">
                    {meta.current_stage_label || '—'}
                  </div>
                  <div className="text-xs text-white/55 mt-1">
                    {meta.startup?.industry} · {meta.startup?.jurisdiction || 'GCC'}
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <div className="text-[9px] uppercase tracking-[0.22em] text-white/45 font-bold">
                    CLEARED MILESTONES
                  </div>
                  <div className="display text-5xl font-bold mt-1">
                    {meta.cleared_count}
                  </div>
                </div>
              </div>

              {meta.note && (
                <div className="mt-6 rounded-lg border border-[#8B3FC5]/30 bg-[#8B3FC5]/10 p-3 text-sm">
                  <p className="italic text-white/80">&ldquo;{meta.note}&rdquo;</p>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold mt-2">
                    Note from sender
                  </p>
                </div>
              )}

              <button
                onClick={download}
                disabled={downloading}
                data-testid="investor-share-download"
                className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-[#8B3FC5] hover:bg-[#5E1F8B] text-white text-sm font-bold uppercase tracking-[0.22em] transition disabled:opacity-50"
              >
                {downloading
                  ? <><Loader2 size={14} className="animate-spin" /> Generating PDF…</>
                  : <><FileDown size={14} /> Download {meta.kind === 'tear_sheet' ? 'tear sheet' : 'DD packet'}</>}
              </button>
            </div>

            {/* Trust line */}
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold flex items-center gap-1.5">
              <ShieldCheck size={11} /> Link expires {new Date(meta.expires_at).toLocaleDateString()} · Every view is logged to the founder&apos;s audit chain.
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
